import { useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useQueryClient } from '@tanstack/react-query'

import type { AffiliateAdminPayoutSchema } from '@/api/gen'
import {
  getGetPartnerStatsViewQueryKey,
  getListPayoutsQueryKey,
  useListPayouts,
  useUpdatePayoutStatus,
} from '@/api/gen'
import { EmptyState } from '@/components/empty-state'
import { InlineQueryError } from '@/components/inline-query-error'
import { MutationError } from '@/components/mutation-error'
import { PayoutDetailDialog } from '@/components/payout-detail-dialog'
import { PayoutsTable } from '@/components/payouts-table'
import { useMutationErrorHandler } from '@/hooks/use-mutation-error-handler'
import { useReportQueryError } from '@/hooks/use-report-query-error'
import { extractValidationDetail } from '@/utils/extract-validation-detail'

export const PayoutsTab = ({ partnerId }: { partnerId: string }) => {
  const queryClient = useQueryClient()
  const params = { partner_id: partnerId }
  const payoutsQuery = useListPayouts(params)
  const { data: payouts, isLoading, isError, refetch } = payoutsQuery
  const [selectedPayout, setSelectedPayout] = useState<AffiliateAdminPayoutSchema | null>(null)
  const actionError = useMutationErrorHandler('updatePayoutStatus failed: ', {
    extractDetail: extractValidationDetail,
  })

  // Forward load failures to Sentry — the bare app QueryClient has no global
  // sink. Previously a failed payouts query fell through to the EmptyState
  // ("No payouts for this partner."), masking the failure as a legitimate
  // empty list and losing the observability signal.
  useReportQueryError(payoutsQuery, 'useListPayouts failed: ')

  const updatePayout = useUpdatePayoutStatus({
    mutation: {
      onMutate: actionError.clearError,
      onSuccess: () => {
        // A status transition (approve/reject/cancel/reverse) moves money between
        // the partner's locked / available / paid buckets, so refresh both the
        // payouts list and the partner-stats view that drives the Overview tab's
        // balance card — a cancel or reverse returns the amount to the balance.
        void queryClient.invalidateQueries({ queryKey: getListPayoutsQueryKey(params) })
        void queryClient.invalidateQueries({
          queryKey: getGetPartnerStatsViewQueryKey(partnerId),
        })
        setSelectedPayout(null)
      },
      onError: actionError.onError,
    },
  })

  const rows = payouts ?? []
  if (isError) {
    return (
      <InlineQueryError
        data-tst="partner-payouts-error"
        messageId="admin.partnerDetail.payouts.loadError"
        onRetry={() => void refetch()}
      />
    )
  }
  if (!isLoading && rows.length === 0) {
    return <EmptyState title={<FormattedMessage id="admin.partnerDetail.payouts.empty" />} />
  }

  return (
    <div className="flex flex-col gap-3">
      <MutationError
        detail={actionError.detail}
        messageId="admin.partnerDetail.payouts.error"
        show={actionError.hasError}
      />
      <PayoutsTable
        showResolvedBy
        data={rows}
        getRowId={(row) => row.id}
        loading={isLoading}
        onRowClick={(clicked) => {
          // Look up the FULL admin row from the source list — the table is
          // pinned to `PayoutRow`, so the dialog needs its admin-only fields
          // (`payout_details`, `original_amount`, `resolved_by_email`) read
          // from the original `AffiliateAdminPayoutSchema` row, not the
          // narrowed clicked row.
          const fullRow = rows.find((p) => p.id === clicked.id)
          if (fullRow == null) {
            return
          }
          setSelectedPayout(fullRow)
        }}
      />

      {selectedPayout && (
        <PayoutDetailDialog
          actionError={actionError}
          loading={updatePayout.isPending}
          payout={selectedPayout}
          onClose={() => setSelectedPayout(null)}
          onAmountAdjusted={(updated) => {
            setSelectedPayout(updated)
            void queryClient.invalidateQueries({ queryKey: getListPayoutsQueryKey(params) })
          }}
          onApprove={(payout) =>
            updatePayout.mutate({ payoutId: payout.id, data: { status: 'PAID' } })
          }
          onCancel={(payout) =>
            updatePayout.mutate({ payoutId: payout.id, data: { status: 'CANCELLED' } })
          }
          onReject={(payout) =>
            updatePayout.mutate({ payoutId: payout.id, data: { status: 'REJECTED' } })
          }
          onReverse={(payout, reason) =>
            updatePayout.mutate({ payoutId: payout.id, data: { status: 'REVERSED', reason } })
          }
        />
      )}
    </div>
  )
}
